import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'
import { Control, Controller } from 'react-hook-form'
import { CampoRegularizacao } from 'src/api/recebedor-pendencia-bacen'
import CurrencyInput from 'src/components/ui/CurrencyInput'
import DateInput from 'src/components/ui/DateInput'
import TextField, { TextFieldSimpleCustom } from 'src/components/ui/TextField'
import UBox from 'src/components/ui/Ubox'
import {
  PREFIXOS_SITE_URL,
  chaveCampoFormulario,
  montarSiteUrl,
  rotuloCampo,
  separarSiteUrl
} from './gruposCampos'

const useStyles = makeStyles(theme => ({
  apoio: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.4,
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(1),
    minHeight: 36
  },
  prefixoUrl: {
    marginRight: theme.spacing(0.5),
    '& .MuiSelect-root': {
      fontSize: 14,
      paddingTop: 2,
      paddingBottom: 2,
      paddingRight: 22,
      color: theme.palette.text.secondary
    }
  },
  campo: {
    marginBottom: 0,
    width: '100%',
    '& > *': {
      width: '100%'
    },
    '& .MuiFormControl-root': {
      width: '100%'
    },
    '& .MuiInputLabel-outlined': {
      maxWidth: 'calc(100% - 28px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      maxWidth: 'calc(133% - 32px)'
    }
  }
}))

type Props = {
  campo: CampoRegularizacao
  pendenciaId: string
  control: Control<Record<string, any>>
  errors: Record<string, any>
  setValue: (name: string, value: any, config?: any) => void
  watch: (name: string) => any
  loading?: boolean
  onCepChange?: (campo: string, cep: string) => void
}

function ehCampoCep(campo: CampoRegularizacao) {
  const sufixo = campo.campo.split('.').pop() || campo.campo
  return campo.tipo === 'cep' || sufixo === 'zip_code'
}

function mascaraCampo(campo: CampoRegularizacao) {
  if (ehCampoCep(campo)) return '99999-999'
  const chave = campo.campo.split('.').pop() || campo.campo
  if (chave === 'document') {
    if (campo.campo.includes('managing_partners')) return '999.999.999-99'
    return campo.rotulo.toLowerCase().includes('cnpj')
      ? '99.999.999/9999-99'
      : '999.999.999-99'
  }
  return undefined
}

function ehCampoEmail(campo: CampoRegularizacao) {
  const chave = campo.campo.split('.').pop() || campo.campo
  return chave.toLowerCase() === 'email'
}

/** Campos que o ViaCEP preenche — sem InputMask (ele só aceita defaultValue e não atualiza). */
function ehCampoEnderecoViaCep(campo: CampoRegularizacao) {
  const sufixo = campo.campo.split('.').pop() || campo.campo
  return ['city', 'state', 'street', 'neighborhood'].includes(sufixo)
}

export default function CampoDinamico({
  campo,
  pendenciaId,
  control,
  errors,
  setValue,
  watch,
  loading,
  onCepChange
}: Props) {
  const classes = useStyles()
  const nomeForm = chaveCampoFormulario(campo.campo)
  const rotulo = rotuloCampo(campo)
  const fieldId = `${pendenciaId}-${campo.campo}`
  const apoioId = `${fieldId}-apoio`
  const erroId = `${fieldId}-erro`
  const erro = errors[nomeForm]
  const describedBy = [apoioId, erro ? erroId : null].filter(Boolean).join(' ')
  const valorAtual = watch(nomeForm) || ''
  const labelProps = { shrink: true }

  return (
    <UBox className={classes.campo}>
      {campo.tipo === 'confirmacao' ? (
        <Controller
          name={nomeForm}
          control={control}
          defaultValue=""
          render={props => (
            <FormControlLabel
              control={
                <Checkbox
                  id={fieldId}
                  color="primary"
                  checked={props.value === true || props.value === 'true'}
                  onChange={e =>
                    props.onChange(e.target.checked ? 'true' : '')
                  }
                  inputProps={
                    {
                      'aria-describedby': describedBy
                    } as any
                  }
                />
              }
              label={rotulo}
            />
          )}
        />
      ) : campo.tipo === 'moeda' ? (
        <CurrencyInput
          id={fieldId}
          name={nomeForm}
          label={rotulo}
          margin="normal"
          value={Number(valorAtual || 0)}
          onValueChange={v => setValue(nomeForm, String(v))}
          erro={errors}
          InputLabelProps={labelProps}
          InputProps={{
            'aria-describedby': describedBy
          } as any}
        />
      ) : campo.tipo === 'data' ? (
        <DateInput
          name={nomeForm}
          label={rotulo}
          value={valorAtual}
          errors={errors}
          control={control}
          handleChange={v => {
            // DateInput trabalha com yyyy-MM-DD; moment(value) sem formato
            // falha em dd/mm/aaaa com dia > 12 (ex.: 20/01/1988) e limpa o campo.
            setValue(nomeForm, v || '', {
              shouldValidate: true,
              shouldDirty: true
            })
          }}
        />
      ) : campo.tipo === 'url' ? (
        <TextFieldSimpleCustom
          id={fieldId}
          name={nomeForm}
          label={rotulo}
          type="text"
          value={separarSiteUrl(valorAtual).resto}
          placeholder="instagram.com/suaclinica"
          onChange={e =>
            setValue(
              nomeForm,
              montarSiteUrl(separarSiteUrl(valorAtual).prefixo, e.target.value),
              {
                shouldValidate: true,
                shouldDirty: true
              }
            )
          }
          erro={errors}
          InputLabelProps={labelProps}
          inputProps={{ 'aria-describedby': describedBy }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" className={classes.prefixoUrl}>
                <Select
                  value={separarSiteUrl(valorAtual).prefixo}
                  onChange={e =>
                    setValue(
                      nomeForm,
                      montarSiteUrl(
                        String(e.target.value),
                        separarSiteUrl(valorAtual).resto
                      ),
                      { shouldValidate: true, shouldDirty: true }
                    )
                  }
                  disableUnderline
                  inputProps={{ 'aria-label': 'Protocolo da URL' }}
                >
                  {PREFIXOS_SITE_URL.map(prefixo => (
                    <MenuItem key={prefixo} value={prefixo}>
                      {prefixo}
                    </MenuItem>
                  ))}
                </Select>
              </InputAdornment>
            )
          }}
        />
      ) : campo.tipo === 'telefone' ? (
        <TextField
          id={fieldId}
          name={nomeForm}
          label={rotulo}
          type="tel"
          control={control}
          erro={errors}
          maskTelCel
          placeholder="(00) 00000-0000"
          InputLabelProps={labelProps}
          InputProps={{
            'aria-describedby': describedBy
          } as any}
        />
      ) : ehCampoCep(campo) ? (
        <Controller
          name={nomeForm}
          control={control}
          defaultValue=""
          render={props => (
            <TextField
              id={fieldId}
              name={nomeForm}
              label={rotulo}
              type="tel"
              value={props.value || ''}
              onChange={e => {
                const valor = e.target.value
                props.onChange(valor)
                onCepChange?.(campo.campo, valor)
              }}
              onBlur={props.onBlur}
              erro={errors}
              disabled={!!loading}
              loading={!!loading}
              mask="99999-999"
              InputLabelProps={labelProps}
              InputProps={{
                'aria-describedby': describedBy
              } as any}
            />
          )}
        />
      ) : ehCampoEnderecoViaCep(campo) || !mascaraCampo(campo) ? (
        <TextFieldSimpleCustom
          id={fieldId}
          name={nomeForm}
          label={rotulo}
          type={ehCampoEmail(campo) ? 'email' : 'text'}
          value={valorAtual}
          onChange={e =>
            setValue(nomeForm, e.target.value, {
              shouldValidate: true,
              shouldDirty: true
            })
          }
          erro={errors}
          InputLabelProps={labelProps}
          inputProps={{ 'aria-describedby': describedBy }}
        />
      ) : (
        <Controller
          name={nomeForm}
          control={control}
          defaultValue=""
          render={props => (
            <TextField
              id={fieldId}
              name={nomeForm}
              label={rotulo}
              value={props.value || ''}
              onChange={e => props.onChange(e.target.value)}
              onBlur={props.onBlur}
              erro={errors}
              mask={mascaraCampo(campo)}
              InputLabelProps={labelProps}
              InputProps={{
                'aria-describedby': describedBy
              } as any}
            />
          )}
        />
      )}

      {!!campo.textoApoio && (
        <Typography
          id={apoioId}
          className={classes.apoio}
          component="p"
          data-testid={`apoio-${campo.campo}`}
        >
          {campo.textoApoio}
        </Typography>
      )}

      {erro?.message && campo.tipo === 'confirmacao' && (
        <FormHelperText id={erroId} error>
          {erro.message}
        </FormHelperText>
      )}
    </UBox>
  )
}
